/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponseObject,
  BEResponsePagedResponseNotificaResponseDTO,
  BEResponseString,
  GetBackNotificheParams,
  NotificaRequestDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Registrazione dell'avvenuta lettura della notifica
 * @summary Registrazione lettura notifica
 */
export const putBackNotificheId = (
  idNotifica: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseObject>(
    {
      url: `/framework-bo-procedimenti/notifiche/${idNotifica}`,
      method: 'put',
    },
    options,
  );
};

export const getPutBackNotificheIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackNotificheId>>,
    TError,
    { idNotifica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackNotificheId>>,
  TError,
  { idNotifica: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackNotificheId>>,
    { idNotifica: string }
  > = props => {
    const { idNotifica } = props ?? {};

    return putBackNotificheId(idNotifica, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackNotificheIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackNotificheId>>
>;

export type PutBackNotificheIdMutationError = ErrorType<unknown>;

/**
 * @summary Registrazione lettura notifica
 */
export const usePutBackNotificheId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackNotificheId>>,
    TError,
    { idNotifica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackNotificheIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Creazione notifica
 * @summary Crea Notifica
 */
export const putBackNotificheCrea = (
  notificaRequestDTO: NotificaRequestDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString>(
    {
      url: `/framework-bo-procedimenti/notifiche/crea`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: notificaRequestDTO,
    },
    options,
  );
};

export const getPutBackNotificheCreaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackNotificheCrea>>,
    TError,
    { data: NotificaRequestDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackNotificheCrea>>,
  TError,
  { data: NotificaRequestDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackNotificheCrea>>,
    { data: NotificaRequestDTO }
  > = props => {
    const { data } = props ?? {};

    return putBackNotificheCrea(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackNotificheCreaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackNotificheCrea>>
>;
export type PutBackNotificheCreaMutationBody = NotificaRequestDTO;
export type PutBackNotificheCreaMutationError = ErrorType<unknown>;

/**
 * @summary Crea Notifica
 */
export const usePutBackNotificheCrea = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackNotificheCrea>>,
    TError,
    { data: NotificaRequestDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackNotificheCreaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Lista Notifiche
 * @summary Elenco Notifiche
 */
export const getBackNotifiche = (
  params?: GetBackNotificheParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponsePagedResponseNotificaResponseDTO>(
    {
      url: `/framework-bo-procedimenti/notifiche`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackNotificheQueryKey = (params?: GetBackNotificheParams) =>
  [
    `/framework-bo-procedimenti/notifiche`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackNotificheQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackNotifiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackNotificheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackNotifiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackNotifiche>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackNotificheQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackNotifiche>>
  > = ({ signal }) => getBackNotifiche(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackNotificheQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackNotifiche>>
>;
export type GetBackNotificheQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Notifiche
 */
export const useGetBackNotifiche = <
  TData = Awaited<ReturnType<typeof getBackNotifiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackNotificheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackNotifiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackNotificheQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
